import { useState } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  FormGroup, Label, Input, Spinner,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { toast } from 'react-toastify';
import { forwardVIRReport } from './FormikHelpers/Api';

const LABEL_STYLE = {
  fontSize: '0.75rem', fontWeight: 700, color: '#334155',
  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
};

const TAG_INPUT_STYLE = {
  display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
  border: '1px solid #E2E8F0', borderRadius: 8, padding: '6px 10px',
  minHeight: 42, background: '#fff', cursor: 'text',
};

const TagInput = ({ tags, onAdd, onRemove, placeholder, type = 'text' }) => {
  const [inputVal, setInputVal] = useState('');

  const add = () => {
    const val = inputVal.trim();
    if (!val) return;
    if (type === 'email') {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(val)) { toast.error('Invalid email address'); return; }
    }
    if (type === 'tel') {
      const phoneRe = /^\+?[\d\s\-().]{7,}$/;
      if (!phoneRe.test(val)) { toast.error('Invalid phone number'); return; }
    }
    if (!tags.includes(val)) onAdd(val);
    setInputVal('');
  };

  return (
    <div style={TAG_INPUT_STYLE} onClick={(e) => e.currentTarget.querySelector('input')?.focus()}>
      {tags.map((tag, i) => (
        <span key={i} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE',
          borderRadius: 20, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600,
        }}>
          {tag}
          <button type="button" onClick={() => onRemove(i)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            display: 'flex', alignItems: 'center', color: '#6366F1',
          }}>
            <FeatherIcon icon="x" size={11} />
          </button>
        </span>
      ))}
      <input
        type={type}
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={tags.length === 0 ? placeholder : ''}
        style={{ border: 'none', outline: 'none', fontSize: '0.83rem', minWidth: 120, flexGrow: 1 }}
      />
    </div>
  );
};

const ForwardReportModal = ({ isOpen, onClose, reportId, reportData }) => {
  const [emails, setEmails] = useState([]);
  const [phones, setPhones] = useState([]);
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (emails.length === 0 && phones.length === 0) {
      toast.error('Add at least one email or phone number');
      return;
    }

    setSending(true);
    try {
      const propertyName = reportData?.property?.buildingName || '';
      const ticketTitle = reportData?.category?.name || reportData?.comments?.slice(0, 60) || 'Inspection';

      await forwardVIRReport(reportId, {
        emails,
        phones,
        subject: subject || undefined,
        message: message || undefined,
        recipientName: recipientName || 'Tenant',
        propertyName,
        unitNo: '',
        ticketTitle,
      });
      handleClose();
    } catch {
      // handled in api layer
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setEmails([]);
    setPhones([]);
    setRecipientName('');
    setSubject('');
    setMessage('');
    onClose();
  };

  const addEmail = (v) => setEmails((p) => [...p, v]);
  const removeEmail = (i) => setEmails((p) => p.filter((_, idx) => idx !== i));
  const addPhone = (v) => setPhones((p) => [...p, v]);
  const removePhone = (i) => setPhones((p) => p.filter((_, idx) => idx !== i));

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="md">
      <ModalHeader
        toggle={handleClose}
        style={{ background: 'linear-gradient(135deg,#0F172A 0%,#1E293B 100%)', borderRadius: '10px 10px 0 0', padding: '16px 24px', borderBottom: '2px solid #4F46E5' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FeatherIcon icon="send" size={16} color="#A5B4FC" />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'white' }}>Forward Report</div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(165,180,252,0.6)', marginTop: 1 }}>Send via Email or SMS</div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody style={{ padding: '24px', background: '#F8FAFC' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <FormGroup style={{ marginBottom: 0 }}>
            <Label style={LABEL_STYLE}>Recipient Name</Label>
            <Input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="e.g. John Smith"
              style={{ borderColor: '#E2E8F0', borderRadius: 8, fontSize: '0.83rem' }}
            />
          </FormGroup>

          <FormGroup style={{ marginBottom: 0 }}>
            <Label style={LABEL_STYLE}>
              Email Addresses
              <span style={{ marginLeft: 6, fontSize: '0.72rem', color: '#94A3B8', fontWeight: 500, textTransform: 'none' }}>
                Press Enter or comma to add
              </span>
            </Label>
            <TagInput
              tags={emails}
              onAdd={addEmail}
              onRemove={removeEmail}
              placeholder="Enter email address..."
              type="email"
            />
          </FormGroup>

          <FormGroup style={{ marginBottom: 0 }}>
            <Label style={LABEL_STYLE}>
              Phone Numbers (SMS)
              <span style={{ marginLeft: 6, fontSize: '0.72rem', color: '#94A3B8', fontWeight: 500, textTransform: 'none' }}>
                Press Enter or comma to add
              </span>
            </Label>
            <TagInput
              tags={phones}
              onAdd={addPhone}
              onRemove={removePhone}
              placeholder="e.g. +1 555 000 1234"
              type="tel"
            />
          </FormGroup>

          <FormGroup style={{ marginBottom: 0 }}>
            <Label style={LABEL_STYLE}>Email Subject (optional)</Label>
            <Input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Vendor Inspection Report — Property Name"
              style={{ borderColor: '#E2E8F0', borderRadius: 8, fontSize: '0.83rem' }}
            />
          </FormGroup>

          <FormGroup style={{ marginBottom: 0 }}>
            <Label style={LABEL_STYLE}>Custom Message (optional)</Label>
            <Input
              type="textarea"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal note to include in the email..."
              style={{ borderColor: '#E2E8F0', borderRadius: 8, fontSize: '0.83rem', resize: 'vertical' }}
            />
          </FormGroup>
        </div>
      </ModalBody>

      <ModalFooter style={{ background: 'white', padding: '14px 24px', gap: 8, borderTop: '1px solid #E2E8F0' }}>
        <button type="button" className="it-btn it-btn--cancel" onClick={handleClose} disabled={sending}>
          <FeatherIcon icon="x" size={13} /> Cancel
        </button>
        <button
          type="button"
          className={`it-btn it-btn--save${sending ? ' it-btn--saving' : ''}`}
          onClick={handleSend}
          disabled={sending}
          style={{ minWidth: 120 }}
        >
          {sending ? <Spinner size="sm" /> : <FeatherIcon icon="send" size={13} />}
          {sending ? 'Sending...' : 'Send Report'}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export { ForwardReportModal };
